/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getDefaultLanguage, translate} from '@ion/core/src/locale/locale';
import UserNotificationDialog from '@ion/dls/src/notifications/UserNotificationDialog';

/**
 * The LanguageChangePrompt component provides the top-level UI prompt within
 * the iOn Application.
 *
 * The prompt visibility is derived on every render rather than in an effect:
 * the persisted default language (localStorage) changes without changing the
 * `user` reference or `i18n.language`, so an effect keyed on those never re-ran.
 */
export const LanguageChangePrompt = () => {
  const {i18n} = useTranslation();
  const currentLanguage = i18n.language;
  const userLanguage = getDefaultLanguage();
  const [dismissedFor, setDismissedFor] = useState<string>(null);
  const showPrompt = currentLanguage !== userLanguage && dismissedFor !== userLanguage;

  const onDismiss = useCallback(() => {
    setDismissedFor(userLanguage);
  }, [userLanguage]);

  const onAction = useCallback(() => {
    window.location.reload();
  }, []);

  if (showPrompt) {
    return (
      <UserNotificationDialog show
        title={translate('common:languageChangePrompt.title')}
        message={translate('common:languageChangePrompt.message')}
        actionLabel={translate('common:languageChangePrompt.actionLabel')}
        dismissLabel={translate('common:languageChangePrompt.dismissLabel')}
        onDismiss={onDismiss}
        onAction={onAction}
        capitalize />
    );
  }
  return null;
};
export default memo(LanguageChangePrompt);
